// FieldVision - Bekræftelsesside for kunder og piloter
// URL: /confirm/:token

import React, { useState, useEffect } from 'react';
import './confirm.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Token info response fra backend
interface TokenInfo {
  token: {
    type: 'CUSTOMER' | 'PILOT_ACCEPT' | 'PILOT_COMPLETE';
    expiresAt: string;
    usedAt: string | null;
    createdAt: string;
    isUsed: boolean;
    isExpired: boolean;
    isValid: boolean;
  };
  order: {
    orderId: number;
    orderNumber: string;
    customerName: string;
    mapType: string;
    totalArea: number;
    status: string;
    pilotName: string | null;
  };
}

// Confirm response fra backend
interface ConfirmResponse {
  success: boolean;
  message: string;
  order: {
    orderId: number;
    orderNumber: string;
    customerName: string;
    oldStatus: string;
    newStatus: string;
  };
  tokenType: string;
}

interface ConfirmPageProps {
  token: string;
}

// State type med ALLE anvendte states
type PageState = 'loading' | 'ready' | 'confirming' | 'confirmed' | 'error';

const ConfirmPage: React.FC<ConfirmPageProps> = ({ token }) => {
  // useState med eksplicit PageState type
  const [state, setState] = useState<PageState>('loading');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [confirmResult, setConfirmResult] = useState<ConfirmResponse | null>(null);
  const [error, setError] = useState<string>('');

  // Hent token info ved load
  useEffect(() => {
    fetchTokenInfo();
  }, [token]);

  const fetchTokenInfo = async () => {
    setState('loading');
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/confirm/${token}/info`);
      const data = await response.json();

      if (!response.ok) {
        // Håndter specifikke fejl
        if (response.status === 404) {
          setError('Linket er ugyldigt eller udløbet');
        } else {
          setError(data.error || 'Der opstod en fejl');
        }
        setState('error');
        return;
      }

      // Tjek om token allerede er brugt
      if (data.token.isUsed) {
        setError('Denne ordre er allerede bekræftet');
        setState('error');
        return;
      }

      // Tjek om token er udløbet
      if (data.token.isExpired) {
        setError('Linket er udløbet');
        setState('error');
        return;
      }

      setTokenInfo(data);
      setState('ready');
    } catch (err) {
      setError('Der opstod en netværksfejl. Prøv igen senere.');
      setState('error');
    }
  };

  const handleConfirm = async () => {
    if (!tokenInfo) return;

    setState('confirming');
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/confirm/${token}`);
      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'Token er allerede brugt') {
          setError('Denne ordre er allerede bekræftet');
        } else {
          setError(data.error || 'Der opstod en fejl ved bekræftelse');
        }
        setState('error');
        return;
      }

      setConfirmResult(data);
      setState('confirmed');
    } catch (err) {
      setError('Der opstod en netværksfejl. Prøv igen.');
      setState('ready');
    }
  };

  // UI helpers
  const getPageTitle = (type: string): string => {
    switch (type) {
      case 'CUSTOMER':
        return 'Bekræft din bestilling';
      case 'PILOT_ACCEPT':
        return 'Acceptér opgave';
      case 'PILOT_COMPLETE':
        return 'Marker opgave som færdig';
      default:
        return 'Bekræftelse';
    }
  };

  const getButtonText = (type: string): string => {
    switch (type) {
      case 'CUSTOMER':
        return 'Bekræft bestilling';
      case 'PILOT_ACCEPT':
        return 'Acceptér opgave';
      case 'PILOT_COMPLETE':
        return 'Marker som færdig';
      default:
        return 'Bekræft';
    }
  };

  const getSuccessTitle = (type: string): string => {
    switch (type) {
      case 'CUSTOMER':
        return 'Tak for din bekræftelse!';
      case 'PILOT_ACCEPT':
        return 'Opgave accepteret!';
      case 'PILOT_COMPLETE':
        return 'Opgave afsluttet!';
      default:
        return 'Bekræftet!';
    }
  };

  // Helper til at tjekke om vi er i confirming state
  const isConfirming = state === 'confirming';

  // Render loading state
  if (state === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div className="confirm-spinner" />
          <p style={styles.loadingText}>Henter information...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (state === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h1 style={styles.errorTitle}>Kunne ikke indlæse</h1>
          <p style={styles.errorMessage}>{error}</p>
          <button style={styles.retryButton} onClick={fetchTokenInfo}>
            Prøv igen
          </button>
        </div>
      </div>
    );
  }

  // Render confirmed state
  if (state === 'confirmed' && confirmResult) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.successTitle}>
            {getSuccessTitle(confirmResult.tokenType)}
          </h1>
          <p style={styles.successMessage}>{confirmResult.message}</p>
          <div style={styles.orderSummary}>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Ordre</span>
              <span style={styles.summaryValue}>#{confirmResult.order.orderNumber}</span>
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Status</span>
              <span style={{ ...styles.statusBadge, ...getStatusStyle(confirmResult.order.newStatus) }}>
                {confirmResult.order.newStatus}
              </span>
            </div>
          </div>
          <p style={styles.closeNote}>Du kan nu lukke dette vindue</p>
        </div>
      </div>
    );
  }

  // Render ready/confirming state - vis ordre info og bekræft knap
  // VIGTIGT: Inkluderer BÅDE 'ready' OG 'confirming' for at TypeScript kan narrowe korrekt
  if ((state === 'ready' || state === 'confirming') && tokenInfo) {
    const { token: tokenData, order } = tokenInfo;
    const isCustomer = tokenData.type === 'CUSTOMER';
    const isPilotAccept = tokenData.type === 'PILOT_ACCEPT';
    const isPilotComplete = tokenData.type === 'PILOT_COMPLETE';

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.logo}>FieldVision</div>
            <h1 style={styles.title}>{getPageTitle(tokenData.type)}</h1>
          </div>

          {/* Order info */}
          <div style={styles.orderInfo}>
            <h2 style={styles.orderTitle}>Ordre #{order.orderNumber}</h2>

            {/* Kunde info - vis til piloter */}
            {(isPilotAccept || isPilotComplete) && (
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Kunde</h3>
                <p style={styles.customerName}>{order.customerName}</p>
              </div>
            )}

            {/* Ordre detaljer */}
            <div style={styles.infoSection}>
              <h3 style={styles.sectionTitle}>Detaljer</h3>
              <div style={styles.detailsGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Type</span>
                  <span style={styles.detailValue}>{order.mapType}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Areal</span>
                  <span style={styles.detailValue}>{order.totalArea.toFixed(1)} ha</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={{ ...styles.statusBadge, ...getStatusStyle(order.status) }}>
                    {order.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Pilot info - vis til kunder når pilot er tildelt */}
            {isCustomer && order.pilotName && (
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Tildelt pilot</h3>
                <p style={styles.pilotName}>{order.pilotName}</p>
              </div>
            )}
          </div>

          {/* Bekræft knap - bruger isConfirming helper */}
          <button
            style={{
              ...styles.confirmButton,
              ...(isConfirming ? styles.confirmButtonDisabled : {}),
            }}
            onClick={handleConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? 'Behandler...' : getButtonText(tokenData.type)}
          </button>

          {/* Info tekst */}
          <p style={styles.infoText}>
            {isCustomer && 'Ved at bekræfte accepterer du vores handelsbetingelser.'}
            {isPilotAccept && 'Ved at acceptere forpligter du dig til at udføre opgaven.'}
            {isPilotComplete && 'Marker kun opgaven som færdig når alt arbejde er udført.'}
          </p>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};

// Status farver
const getStatusStyle = (status: string): React.CSSProperties => {
  const colors: Record<string, { color: string; bgColor: string }> = {
    Modtaget: { color: '#ca8a04', bgColor: '#fef9c3' },
    Bekræftet: { color: '#16a34a', bgColor: '#dcfce7' },
    Behandles: { color: '#2563eb', bgColor: '#dbeafe' },
    Udføres: { color: '#9333ea', bgColor: '#f3e8ff' },
    Færdig: { color: '#6b7280', bgColor: '#f3f4f6' },
  };
  const style = colors[status] || colors.Færdig;
  return {
    color: style.color,
    backgroundColor: style.bgColor,
  };
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
  },
  header: {
    marginBottom: '32px',
  },
  logo: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#16a34a',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '8px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1f2937',
    margin: 0,
  },
  orderInfo: {
    textAlign: 'left',
    marginBottom: '32px',
  },
  orderTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e5e7eb',
  },
  infoSection: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  customerName: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#1f2937',
    margin: 0,
  },
  pilotName: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#1f2937',
    margin: 0,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#1f2937',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
  },
  confirmButton: {
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#16a34a',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginBottom: '16px',
  },
  confirmButtonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
  },
  infoText: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0,
  },
  // Loading state
  loadingText: {
    color: '#6b7280',
    margin: 0,
  },
  // Error state
  errorIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    fontSize: '32px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '8px',
  },
  errorMessage: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px',
  },
  retryButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  // Success state
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    fontSize: '40px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#16a34a',
    marginBottom: '12px',
  },
  successMessage: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  orderSummary: {
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '24px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  summaryLabel: {
    fontSize: '14px',
    color: '#6b7280',
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  closeNote: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0,
  },
};

export default ConfirmPage;
